<template>
  <main class="flex-1 relative overflow-y-auto focus:outline-none" tabindex="0">
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8 ">
      <div class="md:flex md:items-center md:justify-between">
        <div class="flex min-w-0">
          <svg-vue class="content-title-icon" icon="font-awesome.link-solid"></svg-vue>
          <h1 class="content-title">
            {{ $t('Custom pages') }}
          </h1>
        </div>
        <div class="flex md:mt-0 md:ml-4">
          <router-link class="btn btn-purple rounded-none rounded-l-md" to="/admin/custom-pages/new"> {{ $t('Create custom page') }}</router-link>
          <button @click.prevent="$router.back()" type="button" class="btn btn-purple rounded-none rounded-r-md">
            {{ $t('Back') }}
          </button>
        </div>
      </div>
    </div>
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8 ">
      <div class="content-card-body">
        <loading :status="loading"></loading>
        <div class="px-4 py-3 md:w-2/3 items-center md:flex md:justify-between sm:px-6">
          <!-- component -->
          <input
            type="text"
            id="search"
            v-model.lazy="filters.search"
            :placeholder="$t('Search')"
            @change="getCustomPages"
            class="form-input rounded-none rounded-l-md"
          />
          <div class="flex relative w-full">
            <select @change="changeSort" aria-label="Sort by" class="form-input rounded-none" id="status" v-model="sort.column">
              <option value="title">{{ $t('Title') }}</option>
              <option value="created_at">{{ $t('Created at') }}</option>
              <option value="updated_at">{{ $t('Updated at') }}</option>
            </select>
            <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-blue-400">
              <svg class="fill-current h-6 w-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"></path>
              </svg>
            </div>
          </div>
          <button @click="changeSort" class="flex text-gray-800 rounded-none rounded-r-md font-bold py-2 border border-gray-300 px-6" type="button">
            <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.sort-amount-down-alt-regular" v-show="sort.order === 'asc'"></svg-vue>
            <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.sort-amount-up-alt-regular" v-show="sort.order === 'desc'"></svg-vue>
            <span class="ml-2">{{ $t('Sort') }}</span>
          </button>
        </div>
        <template v-if="customPageList.length > 0">
          <ul>
            <template v-for="(customPage, index) in customPageList">
              <li :class="{ 'border-t border-gray-200': index !== 0 }" :key="index">
                <router-link
                  :to="'/admin/custom-pages/' + customPage.id + '/edit'"
                  class="block hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out
                  "
                >
                  <div class="flex items-center px-4 py-4 sm:px-6">
                    <div class="min-w-0 flex-1 flex items-center">
                      <div class="min-w-0 flex-1 r-4 lg:grid lg:grid-cols-2 lg:gap-4">
                        <div>
                          <div class="text-sm font-medium leading-5 text-gray-800 truncate">
                            {{ customPage.title }}
                          </div>
                          <div class="mt-2 flex items-center text-sm leading-5 text-gray-900">
                            <svg-vue class="flex-shrink-0 mr-1.5 h-4 w-4" icon="font-awesome.clock-solid"></svg-vue>
                            {{ customPage.updated_at | momentFormatDateTime }} |
                            <template v-if="customPage.status">
                              <svg-vue class="flex-shrink-0 mx-1.5 h-4 w-4 text-green-400" icon="font-awesome.check-circle-solid"></svg-vue>
                              {{ $t('Page is activated') }}
                            </template>
                            <template v-else>
                              <svg-vue class="flex-shrink-0 mx-1.5 h-4 w-4 text-red-400" icon="font-awesome.times-circle-solid"></svg-vue>
                              {{ $t('Page is disabled') }}
                            </template>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <svg-vue class="h-5 w-5 text-gray-400" icon="font-awesome.angle-right-regular"></svg-vue>
                    </div>
                  </div>
                </router-link>
              </li>
            </template>
          </ul>
        </template>
        <template v-else-if="!loading">
          <div class="h-full flex">
            <div class="m-auto">
              <div class="grid grid-cols-1 justify-items-center h-full w-full px-4 py-10">
                <div class="flex justify-center items-center">
                  <svg-vue class="h-full h-auto w-64 mb-12" icon="undraw.browsing"></svg-vue>
                </div>
                <div class="flex justify-center items-center">
                  <div class="w-full font-semibold text-2xl">
                    {{ $t('No records found') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <nav class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div class="hidden sm:block">
            <p class="text-sm leading-5 text-gray-700">
              {{ $t('Showing') }}
              <span class="font-medium">{{ pagination.perPage * pagination.currentPage - pagination.perPage + 1 }}</span>
              {{ $t('to') }}
              <span class="font-medium">{{
                pagination.perPage * pagination.currentPage <= pagination.total ? pagination.perPage * pagination.currentPage : pagination.total
              }}</span>
              {{ $t('of') }}
              <span class="font-medium">{{ pagination.total }}</span>
              {{ $t('results') }}
            </p>
          </div>
          <div class="flex-1 flex justify-between sm:justify-end">
            <button
              :class="pagination.currentPage <= 1 ? 'opacity-50 cursor-not-allowed' : ''"
              :disabled="pagination.currentPage <= 1"
              @click="changePage(pagination.currentPage - 1)"
              class="pagination-link"
              type="button"
            >
              {{ $t('Previous') }}
            </button>
            <button
              :class="pagination.currentPage >= pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''"
              :disabled="pagination.currentPage >= pagination.totalPages"
              @click="changePage(pagination.currentPage + 1)"
              class="ml-3 pagination-link"
              type="button"
            >
              {{ $t('Next') }}
            </button>
          </div>
        </nav>
      </div>
    </div>
  </main>
</template>
<script>
export default {
  name: 'list',
  metaInfo() {
    return {
      title: this.$i18n.t('Quick replies'),
    };
  },
  data() {
    return {
      page: 1,
      filters: {
        search: null,
      },
      sort: {
        order: 'asc',
        column: 'created_at',
      },
      perPage: 10,
      loading: true,
      pagination: {
        currentPage: 0,
        perPage: 0,
        total: 0,
        totalPages: 0,
      },
      customPageList: [],
    };
  },
  filters: {
    momentFormatDateTime(value) {
      return moment(value)
        .locale(window.app.app_date_locale)
        .format(`${window.app.app_date_format} HH:mm`);
    },
    momentFormatDateTimeAgo(value) {
      return moment(value)
        .locale(window.app.app_date_locale)
        .fromNow();
    },
  },
  mounted() {
    this.getCustomPages();
  },
  methods: {
    changePage(page) {
      if (page > 0 && page <= this.pagination.totalPages && page !== this.page) {
        this.page = page;
        this.getCustomPages();
      }
    },
    changeSort() {
      if (this.sort.order === 'asc') {
        this.sort.order = 'desc';
      } else if (this.sort.order === 'desc') {
        this.sort.order = 'asc';
      }
      this.getCustomPages();
    },
    getCustomPages() {
      this.loading = true;
      axios
        .get(route('custom-pages.index'), {
          params: {
            page: this.page,
            sort: this.sort,
            perPage: this.perPage,
            search: this.filters.search,
          },
        })
        .then((response) => {
          this.customPageList = response.data.items;
          this.pagination = response.data.pagination;
          if (this.pagination.totalPages < this.pagination.currentPage) {
            this.page = this.pagination.totalPages;
            this.getCustomPages();
          } else {
            this.loading = false;
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>
